import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface HelpBubbleProps {
  message?: string;
  delay?: number;
}

export const HelpBubble = ({ 
  message = "Not sure where to start? Try entering your suburb or date above.",
  delay = 2000 
}: HelpBubbleProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed it before
    const dismissed = localStorage.getItem('help-bubble-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Delay appearance for subtlety
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('help-bubble-dismissed', 'true');
  };

  if (isDismissed || !isVisible) return null;

  return (
    <div 
      className="fixed bottom-6 right-6 z-40 animate-in fade-in slide-in-from-bottom-4 duration-500 md:bottom-6 md:right-6 max-md:bottom-24 max-md:right-4"
      style={{ maxWidth: '320px' }}
    >
      <Card className="bg-white/95 backdrop-blur-sm border border-brand-primary/20 shadow-lg p-3 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="rounded-full bg-[#967F81]/10 p-2">
              <Sparkles className="h-4 w-4 text-brand-primary" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 leading-relaxed">
              {message}
            </p>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="flex-shrink-0 h-6 w-6 p-0 hover:bg-gray-100 rounded-full"
          >
            <X className="h-4 w-4 text-gray-400" />
          </Button>
        </div>
      </Card>
    </div>
  );
};
